import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Card from '../common/Card'
import ParrotMark from '../common/ParrotMark'
import UserLink from './UserLink'
import UserPhoto from './UserPhoto'
import { seedOf, waveformBars } from '../../lib/archive'
import { faDigits } from '../../lib/numerals'
import { bandOf } from '../../lib/scoreBand'
import { COMMENT_MAX_LENGTH, canClap, canRate, faDecimal, whenLabel } from '../../lib/social'

const WAVE_BARS = 30

const BAND_INK = { good: 'text-good', warn: 'text-warn', bad: 'text-bad' }
const BAND_BAR = { good: 'bg-good', warn: 'bg-warn', bad: 'bg-bad' }

/** The parrot mark on the avatar disc. */
function AvatarDisc({ avatar, size = 42 }) {
  const tone =
    { classic: 'bg-brand-50 border-brand-600', mint: 'bg-good-tint border-good', gold: 'bg-accent-tint border-accent', sunset: 'bg-bad-tint border-bad', night: 'bg-surface-3 border-brand-800', aurora: 'bg-brand-50 border-brand-600', ember: 'bg-bad-tint border-bad', mythic: 'bg-accent-tint border-accent', legend: 'bg-accent-tint border-accent-ink' }[avatar] ??
    'bg-brand-50 border-brand-600'
  return (
    <span className={`grid shrink-0 place-items-center rounded-full border-2 ${tone}`} style={{ width: size, height: size }} aria-hidden="true">
      <ParrotMark size={size * 0.57} />
    </span>
  )
}

function Star({ filled }) {
  return (
    <svg width="19" height="19" viewBox="0 0 24 24" aria-hidden="true" className={filled ? 'text-accent' : 'text-track'}>
      <path
        d="M12 2.6l2.5 6 6.5.5-4.9 4.2 1.5 6.3L12 16.2l-5.6 3.4 1.5-6.3L3 9.1l6.5-.5z"
        fill="currentColor"
      />
    </svg>
  )
}

/**
 * One shared take (design page 10): the author, the line, the play row,
 * five stars, claps, the thread. Every tap goes up to the page, which asks
 * the server and hands back the changed card; the card itself decides
 * nothing except what to draw.
 */
export default function PostCard({ post, player, busy, onClap, onRate, onComment, onFollow, onUnshare }) {
  const { t } = useTranslation('social')
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState('')
  const band = bandOf(post.score)
  const bars = useMemo(() => waveformBars(WAVE_BARS, seedOf(post.id), 18), [post.id])
  const owns = player.playingId === post.id
  const playing = owns && player.isPlaying
  const progress = owns ? player.progress : 0
  const rating = post.myRating ?? 0
  const heard = player.hasHeard?.(post.id) === true
  const rateable = canRate(post, heard)
  const clappable = canClap(post, heard)
  const mustListen = !post.author.me && !rateable

  function send() {
    const text = draft.trim()
    if (!text || busy) return
    setDraft('')
    onComment(post, text)
  }

  return (
    <Card as="article" pad="p-3.5" className={`space-y-3 transition-opacity ${busy ? 'opacity-70' : ''}`}>
      <header className="flex items-center gap-2.5">
        <UserLink userId={post.author.id} className="shrink-0 rounded-full" label={post.author.displayName}>
          <UserPhoto photoUrl={post.author.photoUrl} size={42}>
            <AvatarDisc avatar={post.author.avatar} />
          </UserPhoto>
        </UserLink>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <UserLink userId={post.author.id} className="text-[13.5px] font-bold text-ink">
              {post.author.displayName}
            </UserLink>
            <span className="inline-flex items-center gap-0.5 rounded-full bg-accent-tint px-1.5 py-0.5 text-[10px] font-bold text-accent-ink">
              🔥 {faDigits(post.author.streakDays)}
            </span>
            {!post.author.me && (
              <button
                type="button"
                disabled={busy}
                onClick={() => onFollow(post.author, !post.author.followedByMe)}
                className={`rounded-full px-1.5 py-0.5 text-[10.5px] font-bold ${post.author.followedByMe ? 'text-ink-3' : 'text-brand-700'}`}
              >
                {post.author.followedByMe ? t('postCard.following') : t('postCard.follow')}
              </button>
            )}
          </div>
          <p className="text-[11px] text-ink-3">
            {whenLabel(post.createdAt)} · {post.lessonTitleFa || <span dir="ltr">{post.lessonTitleEn}</span>}
          </p>
        </div>
        <div className="text-center">
          <p className={`tnum text-base font-bold ${BAND_INK[band] ?? 'text-ink'}`}>{faDigits(post.score)}</p>
          <p className="text-[9px] text-ink-3">{t('postCard.scoreLabel')}</p>
        </div>
      </header>

      <p className="lesson-text ltr text-[14.5px] leading-relaxed text-ink-2">{post.sentenceTextEn}</p>

      <div className="flex items-center gap-2.5 rounded-[14px] bg-surface-2 px-3 py-2.5">
        <button
          type="button"
          onClick={() => player.toggle(post)}
          aria-label={playing ? t('postCard.pauseAria') : t('postCard.playAria')}
          className={`grid size-10 shrink-0 place-items-center rounded-full ${playing ? 'bg-brand-600 text-white dark:text-slate-900' : 'border border-line bg-surface text-brand-700'}`}
        >
          {playing ? (
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 5h4v14H7zM13 5h4v14h-4z" /></svg>
          ) : (
            <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>
          )}
        </button>
        <div className="ltr flex h-7 flex-1 items-end gap-[2px]" aria-hidden="true">
          {bars.map((height, index) => {
            const played = progress > 0 && index / WAVE_BARS <= progress
            return <span key={index} className={`flex-1 rounded-sm ${played ? (BAND_BAR[band] ?? 'bg-brand-600') : 'bg-wave-off'}`} style={{ height }} />
          })}
        </div>
        <span className="ltr tnum text-[11px] text-ink-3">{clock(post.audioDurationSeconds ?? 0)}</span>
      </div>

      <div className="flex items-center gap-2">
        <div
          className={`flex flex-1 gap-0.5 ${mustListen ? 'opacity-50' : ''}`}
          role={post.author.me ? undefined : 'radiogroup'}
          aria-label={t('postCard.fiveStarAria')}
          title={mustListen ? t('postCard.listenFirst') : undefined}
          data-listen-first={mustListen ? 'true' : undefined}
        >
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              disabled={busy || !rateable}
              aria-label={t('postCard.starAria', { star: faDigits(star) })}
              aria-pressed={rating === star}
              onClick={() => onRate(post, star)}
              className="grid size-[30px] place-items-center rounded-lg disabled:cursor-default"
            >
              <Star filled={star <= rating} />
            </button>
          ))}
        </div>
        {post.averageRating != null && <span className="text-[11px] text-ink-3">{t('postCard.average', { value: faDecimal(post.averageRating) })}</span>}
      </div>

      <div className="h-px bg-line" />

      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={busy || !clappable}
          onClick={() => onClap(post, !post.clappedByMe)}
          aria-pressed={post.clappedByMe}
          title={clappable ? undefined : t('postCard.listenFirst')}
          data-clap-listen-first={clappable ? undefined : 'true'}
          className={`tnum flex h-[34px] items-center gap-1.5 rounded-full px-3 text-xs font-semibold disabled:opacity-50 ${post.clappedByMe ? 'bg-bad-tint text-bad-ink' : 'bg-surface-2 text-ink-2'}`}
        >
          <span aria-hidden="true">{post.clappedByMe ? '♥' : '♡'}</span>
          {faDigits(post.claps)}
        </button>
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          className="tnum flex h-[34px] items-center gap-1.5 rounded-full bg-surface-2 px-3 text-xs font-semibold text-ink-2"
        >
          <span aria-hidden="true">💬</span>
          {faDigits(post.commentCount)}
        </button>
        <span className="flex-1" />
        {/* R13: no unshare while the server posts every take itself — the page then passes no handler. */}
        {post.author.me && onUnshare && (
          <button type="button" disabled={busy} onClick={() => onUnshare(post)} className="rounded-full px-1.5 py-1 text-[11px] font-semibold text-bad-ink">
            {t('postCard.unshare')}
          </button>
        )}
        <span className="tnum text-[11px] text-ink-3">{t('postCard.playsSuffix', { count: faDigits(post.plays) })}</span>
      </div>

      {open && (
        <div className="space-y-2">
          {(post.comments ?? []).map((comment) => (
            <div key={comment.id} className={`flex items-start gap-2 ${comment.author.me ? 'flex-row-reverse' : ''}`}>
              <UserLink userId={comment.author.id} className="shrink-0 rounded-full" label={comment.author.displayName}>
                <UserPhoto photoUrl={comment.author.photoUrl} size={28}>
                  <AvatarDisc avatar={comment.author.avatar} size={28} />
                </UserPhoto>
              </UserLink>
              <div className={`max-w-[260px] space-y-1 rounded-[14px] px-3 py-2 ${comment.author.me ? 'bg-brand-600 text-white dark:text-slate-900' : 'bg-surface-2 text-ink'}`}>
                <p className="text-[11px] font-bold opacity-75">{comment.author.displayName}</p>
                <p className="text-[12.5px] leading-[1.85]">{comment.text}</p>
              </div>
            </div>
          ))}
          <form
            className="flex items-center gap-1.5 rounded-full bg-surface-2 py-1 pe-1.5 ps-3.5"
            onSubmit={(event) => {
              event.preventDefault()
              send()
            }}
          >
            <input
              value={draft}
              onChange={(event) => setDraft(event.target.value.slice(0, COMMENT_MAX_LENGTH))}
              placeholder={t('postCard.commentPlaceholder')}
              aria-label={t('postCard.commentAria')}
              className="min-w-0 flex-1 bg-transparent text-[12.5px] text-ink outline-none placeholder:text-ink-3"
            />
            <button
              type="submit"
              disabled={busy || !draft.trim()}
              aria-label={t('postCard.sendCommentAria')}
              className="grid size-[34px] place-items-center rounded-full bg-brand-600 text-white disabled:bg-track disabled:text-ink-3 dark:text-slate-900"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 15V3M8 7l4-4 4 4M4 15v4a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-4" />
              </svg>
            </button>
          </form>
        </div>
      )}
    </Card>
  )
}

/** m:ss in Latin digits, like every duration. */
function clock(seconds) {
  const total = Math.max(0, Math.round(seconds))
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}
